import React, { useState, useEffect } from "react";
import {
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Coffee,
  Heart,
  Download,
  Check,
} from "lucide-react";
import "./styles.css";

const YourFreelanceStory = () => {
  const [currentPage, setCurrentPage] = useState("welcome");
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState(() => {
    const saved = localStorage.getItem("freelanceStoryResponses");
    return saved ? JSON.parse(saved) : {};
  });
  const [personalStory, setPersonalStory] = useState("");
  const [storyLoading, setStoryLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    fullName: "",
    email: "",
    followUpInterview: false,
    generalContact: false,
  });

  // Save responses to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("freelanceStoryResponses", JSON.stringify(responses));
  }, [responses]);

  // TALATERRA Brand Colors
  const colors = {
    slate: "#0C3D34",
    charcoal: "#040404",
    cream: "#FFFAF6",
    sage: "#1A554D",
    peru: "#B26E37",
    powder: "#A0C9CD",
    violet: "#8B7BB8",
    warmCream: "#F5EDE4",
    lightBlue: "#E8F4F5",
  };

  const clearAllResponses = () => {
    if (
      window.confirm(
        "Are you sure you want to clear all your responses and start fresh? This cannot be undone."
      )
    ) {
      localStorage.removeItem("freelanceStoryResponses");
      setResponses({});
      setCurrentPage("welcome");
      setCurrentSection(0);
      setCurrentQuestion(0);
      alert("All responses cleared. Starting fresh!");
    }
  };

  const sections = [
    {
      title: "Your Story Begins",
      subtitle: "Let's start with who you are",
      icon: "○",
      questions: [
        {
          q: "Introduce yourself",
          hint: "Tell us your story—where you are, what lights you up, how you ended up here",
        },
        {
          q: "What do you do?",
          hint: "Paint us a picture of your work in the world",
        },
        {
          q: "Do you think of yourself as a freelancer or as something else?",
          hint: "Labels matter. What feels true for you?",
        },
        {
          q: "How long have you been freelancing?",
          hint: "Every journey has a beginning",
        },
      ],
    },
    {
      title: "Your Why",
      subtitle: "The change you're here to create",
      icon: "↑",
      questions: [
        {
          q: "What change do you want to create?",
          hint: "When you imagine your work's ripple effect, what do you see?",
        },
        {
          q: "Was becoming a freelancer a clear career objective or a reaction to an event?",
          hint: "Sometimes we choose the path. Sometimes the path chooses us.",
        },
      ],
    },
    {
      title: "The Reality Check",
      subtitle: "Expectations meet experience",
      icon: "◊",
      cosmoHook: "Plot twist: What you discover here might surprise you...",
      questions: [
        {
          q: "When you began, what did you believe it was going to be like?",
          hint: "Remember those early dreams and assumptions?",
        },
        {
          q: "What's the difference between what you believed it was going to be like and what it's actually like? How does this show up for you everyday?",
          hint: "The gap between expectation and reality—where does it show up?",
        },
        {
          q: "What if you had the chance to start all over again? What would you do differently? Why? What difference do you believe this would have made?",
          hint: "Hindsight is a teacher. What has it taught you?",
        },
      ],
    },
    {
      title: "Your Daily Rhythm",
      subtitle: "How your work actually works",
      icon: "⟳",
      questions: [
        {
          q: "How does your week work?",
          hint: "Describe the flow and structure (or beautiful chaos) of your days",
        },
        {
          q: "How many hours per day do you work?",
          hint: "The honest answer, including the invisible work",
        },
        {
          q: "How much time do you spend looking for new clients?",
          hint: "The hustle behind the work",
        },
        {
          q: "Do you provide technical skills (fix this, fix that)?",
          hint: "Are you the problem-solver?",
        },
        {
          q: "Are you providing consultative skills?",
          hint: "Are you the guide and strategist?",
        },
        {
          q: "What persists in your relationship with clients?",
          hint: "What keeps showing up, again and again?",
        },
      ],
    },
    {
      title: "What You've Learned",
      subtitle: "The wisdom of experience",
      icon: "◉",
      cosmoHook: "You're about to uncover what sets you apart...",
      questions: [
        {
          q: "What have you discovered works best?",
          hint: "Your greatest hits—what lights you up AND pays the bills?",
        },
        {
          q: "What have you discovered works worse?",
          hint: "The lessons that cost you something to learn",
        },
        {
          q: "What's the ideal contract?",
          hint: "If you could design it perfectly...",
        },
        {
          q: "What's the least enjoyed contract?",
          hint: "The one that makes you think 'never again'",
        },
      ],
    },
    {
      title: "The Struggle",
      subtitle: "Where it gets real",
      icon: "~",
      questions: [
        {
          q: "What are your greatest frustrations?",
          hint: "The honest, unvarnished truth",
        },
        {
          q: "What have you done to solve these frustrations?",
          hint: "You're more resourceful than you think",
        },
      ],
    },
    {
      title: "The Trade-offs",
      subtitle: "What you've gained, what you've given up",
      icon: "⊕",
      cosmoHook:
        "Almost there! This last section reveals your true priorities...",
      questions: [
        {
          q: "What benefits have you accrued as a freelancer?",
          hint: "The gifts of this path—freedom, flexibility, and...?",
        },
        {
          q: "What benefits have you been deprived of as a freelancer?",
          hint: "What do you miss? What do you long for?",
        },
      ],
    },
  ];

  const totalQuestions = sections.reduce(
    (sum, section) => sum + section.questions.length,
    0
  );

  const getCurrentQuestionNumber = () => {
    let count = 0;
    for (let i = 0; i < currentSection; i++) {
      count += sections[i].questions.length;
    }
    return count + currentQuestion + 1;
  };

  const handleResponseChange = (value) => {
    const key = `${currentSection}-${currentQuestion}`;
    setResponses((prev) => ({ ...prev, [key]: value }));
  };

  const getCurrentResponse = () => {
    const key = `${currentSection}-${currentQuestion}`;
    return responses[key] || "";
  };

  const nextQuestion = () => {
    if (currentQuestion < sections[currentSection].questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else if (currentSection < sections.length - 1) {
      setCurrentSection((prev) => prev + 1);
      setCurrentQuestion(0);
    } else {
      generatePersonalStory();
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    } else if (currentSection > 0) {
      setCurrentSection((prev) => prev - 1);
      setCurrentQuestion(sections[currentSection - 1].questions.length - 1);
    }
  };

  const generatePersonalStory = async () => {
    setCurrentPage("generating");
    setStoryLoading(true);

    let summary =
      "Your journey as a freelance environmental educator is taking shape. ";

    const intro = responses["0-0"] || "";
    const changeGoal = responses["1-0"] || "";
    const expectations = responses["2-0"] || "";
    const reality = responses["2-1"] || "";
    const worksWell = responses["4-0"] || "";
    const frustrations = responses["5-0"] || "";
    const benefits = responses["6-0"] || "";
    const tradeoffs = responses["6-1"] || "";

    if (intro)
      summary += "You've shared your story with honesty and reflection. ";
    if (changeGoal)
      summary += "The change you want to create drives your work forward. ";
    if (expectations && reality)
      summary +=
        "You've navigated the gap between what you expected and what freelancing actually demands—that awareness is powerful. ";
    if (worksWell)
      summary +=
        "You've discovered what works for you, and that knowledge is your competitive advantage. ";
    if (frustrations)
      summary +=
        "You've faced frustrations head-on, showing resilience in building your practice. ";
    if (benefits || tradeoffs)
      summary +=
        "You understand the trade-offs of freelancing—the freedoms you've gained and what you've given up. This clarity helps you make intentional choices. ";

    summary +=
      "\n\nLooking at your complete responses, patterns emerge: You're building a practice that aligns with your values while navigating the practical realities of independent work. You're learning what serves you and what doesn't. You're making choices that reflect both who you are and who you're becoming.\n\n";
    summary +=
      "Your path is uniquely yours. The insights you've captured here are a snapshot of where you are right now—refer back to them when you need clarity, when you're making decisions, or when you want to measure how far you've come. This reflection is a gift you've given yourself.";

    setPersonalStory(summary);
    setStoryLoading(false);
    setCurrentPage("story");
  };

  const downloadResponses = () => {
    let content = "YOUR FREELANCE STORY\n\n";
    content += "=".repeat(70) + "\n\n";
    content += "YOUR PERSONALIZED STORY:\n\n";
    content += personalStory + "\n\n";
    content += "=".repeat(70) + "\n\n";
    content += "YOUR COMPLETE RESPONSES:\n\n";

    sections.forEach((section, sIdx) => {
      content += `${section.icon} ${section.title.toUpperCase()}\n`;
      content += "-".repeat(70) + "\n\n";
      section.questions.forEach((question, qIdx) => {
        const key = `${sIdx}-${qIdx}`;
        content += `Q: ${question.q}\n\n`;
        content += `A: ${responses[key] || "[No response provided]"}\n\n`;
      });
      content += "\n";
    });

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my-freelance-story.txt";
    a.click();
  };

  const submitToGoogleForm = () => {
    let allResponsesText = "";
    sections.forEach((section, sIdx) => {
      allResponsesText += `\n${section.title}\n${"=".repeat(50)}\n\n`;
      section.questions.forEach((question, qIdx) => {
        const key = `${sIdx}-${qIdx}`;
        const response = responses[key] || "[No response]";
        allResponsesText += `Q: ${question.q}\n\nA: ${response}\n\n`;
      });
    });

    const baseUrl =
      "https://docs.google.com/forms/d/e/1FAIpQLSeFYbdCRgnMvkGc_--Ma0L5Nj3AMKjgu6BVUb1LJiz85f-88Q/viewform";

    const params = new URLSearchParams({
      "entry.677016687": contactInfo.fullName || "",
      "entry.177134838": contactInfo.email || "",
      "entry.1817608959": contactInfo.followUpInterview
        ? "I'm willing to participate in a follow-up interview to discuss my responses in more depth."
        : "",
      "entry.1590131332": contactInfo.generalContact
        ? "I'd like to receive general updates about this research and TALATERRA resources."
        : "",
      "entry.1700293834": allResponsesText,
    });

    window.open(`${baseUrl}?${params.toString()}`, "_blank");
    setCurrentPage("thankyou-research");
  };

  // Common Styles
  const containerStyle = {
    minHeight: "100vh",
    padding: "1.5rem",
    background: `linear-gradient(135deg, ${colors.cream} 0%, ${colors.warmCream} 50%, ${colors.lightBlue} 100%)`,
  };

  const cardStyle = {
    background: "white",
    borderRadius: "1rem",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    padding: "2rem",
    maxWidth: "48rem",
    margin: "0 auto",
  };

  const btnPrimaryStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    width: "100%",
    padding: "1rem 1.5rem",
    background: `linear-gradient(to right, ${colors.slate}, ${colors.sage})`,
    color: "white",
    border: "none",
    borderRadius: "0.75rem",
    fontSize: "1.125rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
  };

  const btnSecondaryStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.5rem",
    background: "white",
    color: colors.sage,
    border: `2px solid ${colors.sage}`,
    borderRadius: "0.75rem",
    fontSize: "1rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s",
  };

  // WELCOME PAGE
  if (currentPage === "welcome") {
    const hasExistingResponses = Object.keys(responses).length > 0;

    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, borderTop: `4px solid ${colors.peru}` }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "1rem",
            }}
          >
            <Sparkles style={{ color: colors.peru }} size={32} />
            <h1
              style={{
                fontSize: "2rem",
                fontWeight: "bold",
                background: `linear-gradient(to right, ${colors.slate}, ${colors.sage})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Discover Your Freelance Story
            </h1>
          </div>

          <p
            style={{
              fontSize: "1.25rem",
              color: colors.charcoal,
              marginBottom: "1.5rem",
              lineHeight: "1.75",
            }}
          >
            Gain clarity on your path forward as a freelance environmental
            educator
          </p>

          {hasExistingResponses && (
            <div
              style={{
                background: `${colors.peru}15`,
                border: `2px solid ${colors.peru}40`,
                borderRadius: "0.75rem",
                padding: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <p style={{ color: colors.slate, fontWeight: "600" }}>
                ○ Welcome back! Your previous responses are saved and waiting
                for you.
              </p>
            </div>
          )}

          <div
            style={{
              background: `${colors.peru}15`,
              border: `2px solid ${colors.peru}40`,
              borderRadius: "0.75rem",
              padding: "1.5rem",
              marginBottom: "1.5rem",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}
            >
              <Coffee style={{ color: colors.peru, flexShrink: 0 }} size={32} />
              <div>
                <h3
                  style={{
                    color: colors.slate,
                    fontWeight: "600",
                    marginBottom: "0.5rem",
                  }}
                >
                  Your Personal Takeaway
                </h3>
                <p style={{ color: colors.charcoal, lineHeight: "1.75" }}>
                  By the end of this reflection, you'll receive a{" "}
                  <strong>personalized narrative summary</strong> that reveals
                  patterns in your freelance journey—showing where you've been,
                  what you've learned, and where you might be heading. It's like
                  having a conversation with your future self.
                </p>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <h3
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                color: colors.slate,
                fontWeight: "600",
                marginBottom: "1rem",
              }}
            >
              <Heart style={{ color: colors.peru }} size={20} />
              What You'll Discover:
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "1rem",
              }}
            >
              {[
                {
                  icon: "○",
                  text: "Your freelance origin story",
                  sub: "Was this a choice or did the path choose you?",
                  bg: colors.powder,
                },
                {
                  icon: "◉",
                  text: "What sets you apart",
                  sub: "What's working brilliantly (and what's not)",
                  bg: colors.sage,
                },
                {
                  icon: "⊕",
                  text: "Your trade-offs",
                  sub: "What you've gained and what you've given up",
                  bg: colors.peru,
                },
                {
                  icon: "↑",
                  text: "Your next chapter",
                  sub: "Clarity on where you're headed",
                  bg: colors.violet,
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    background: `${item.bg}20`,
                    borderRadius: "0.75rem",
                    padding: "1rem",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.75rem",
                  }}
                >
                  <span style={{ fontSize: "1.5rem", color: item.bg }}>
                    {item.icon}
                  </span>
                  <p style={{ color: colors.charcoal }}>
                    <strong>{item.text}</strong> — {item.sub}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              background: `${colors.warmCream}`,
              border: `1px solid ${colors.peru}40`,
              borderRadius: "0.75rem",
              padding: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <p
              style={{
                fontSize: "0.875rem",
                color: colors.charcoal,
                marginBottom: "0.75rem",
              }}
            >
              <strong>Think of this as your personal retreat</strong> — Pour
              yourself a cup of tea, find a quiet corner, and give yourself
              20-25 minutes of honest reflection. There are no right answers
              here, just your truth.
            </p>
            <p style={{ fontSize: "0.875rem", color: colors.sage }}>
              <strong>Your progress saves automatically</strong> as you type. If
              you need to step away, your responses will be here when you
              return—on this same device and browser.
            </p>
          </div>

          <div
            style={{
              background: `${colors.powder}20`,
              border: `1px solid ${colors.powder}`,
              borderRadius: "0.75rem",
              padding: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <p style={{ fontSize: "0.875rem", color: colors.slate }}>
              <strong>○ Join freelance environmental educators</strong> who've
              already discovered their story and gained clarity on their path
              forward.
            </p>
          </div>

          <button
            onClick={() => setCurrentPage("survey")}
            style={btnPrimaryStyle}
          >
            {hasExistingResponses
              ? "Continue Your Journey"
              : "Begin Your Journey"}{" "}
            <ChevronRight size={24} />
          </button>
        </div>

        <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
          <p
            style={{
              color: colors.sage,
              fontSize: "0.875rem",
              marginBottom: "0.75rem",
            }}
          >
            🌿 A tool by TALATERRA for freelance environmental educators 🌿
          </p>
          {Object.keys(responses).length > 0 && (
            <button
              onClick={clearAllResponses}
              style={{
                ...btnSecondaryStyle,
                fontSize: "0.875rem",
                padding: "0.5rem 1rem",
              }}
            >
              Start Fresh / Clear Responses
            </button>
          )}
        </div>
      </div>
    );
  }

  // SURVEY PAGE
  if (currentPage === "survey") {
    const questionNumber = getCurrentQuestionNumber();
    const progress = (questionNumber / totalQuestions) * 100;
    const currentQ = sections[currentSection].questions[currentQuestion];
    const showCosmoHook =
      currentQuestion === 0 && sections[currentSection].cosmoHook;

    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: "48rem", margin: "0 auto" }}>
          {/* Progress Bar */}
          <div
            style={{
              background: "white",
              borderRadius: "0.75rem",
              padding: "1rem",
              marginBottom: "1rem",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "0.5rem",
              }}
            >
              <span style={{ fontSize: "0.875rem", color: colors.sage }}>
                Question {questionNumber} of {totalQuestions}
              </span>
              <span style={{ fontSize: "0.875rem", color: colors.sage }}>
                {Math.round(progress)}% complete
              </span>
            </div>
            <div
              style={{
                background: "#E5E7EB",
                borderRadius: "9999px",
                height: "0.5rem",
              }}
            >
              <div
                style={{
                  background: `linear-gradient(to right, ${colors.sage}, ${colors.powder})`,
                  borderRadius: "9999px",
                  height: "100%",
                  width: `${progress}%`,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>

          {/* Section Header */}
          <div
            style={{
              background: `linear-gradient(to right, ${colors.slate}, ${colors.sage})`,
              borderRadius: "0.75rem",
              padding: "1rem",
              marginBottom: "1rem",
              color: "white",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              <span style={{ fontSize: "1.5rem" }}>
                {sections[currentSection].icon}
              </span>
              <div>
                <h3 style={{ fontWeight: "600", fontSize: "1.125rem" }}>
                  {sections[currentSection].title}
                </h3>
                <p style={{ opacity: 0.9, fontSize: "0.875rem" }}>
                  {sections[currentSection].subtitle}
                </p>
              </div>
            </div>
            {showCosmoHook && (
              <div
                style={{
                  marginTop: "0.75rem",
                  padding: "0.75rem",
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Sparkles size={16} />
                <span style={{ fontSize: "0.875rem", fontStyle: "italic" }}>
                  {sections[currentSection].cosmoHook}
                </span>
              </div>
            )}
          </div>

          {/* Question Card */}
          <div style={{ ...cardStyle, borderTop: `4px solid ${colors.peru}` }}>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                color: colors.slate,
                marginBottom: "0.5rem",
              }}
            >
              {currentQ.q}
            </h2>
            <p
              style={{
                color: colors.sage,
                fontStyle: "italic",
                marginBottom: "1.5rem",
              }}
            >
              {currentQ.hint}
            </p>

            <textarea
              value={getCurrentResponse()}
              onChange={(e) => handleResponseChange(e.target.value)}
              style={{
                width: "100%",
                minHeight: "200px",
                padding: "1rem",
                border: `2px solid #E5E7EB`,
                borderRadius: "0.75rem",
                fontSize: "1rem",
                lineHeight: "1.75",
                resize: "vertical",
                marginBottom: "1.5rem",
                outline: "none",
              }}
              placeholder="Take your time... there's no rush. Write what feels true."
              onFocus={(e) => (e.target.style.borderColor = colors.powder)}
              onBlur={(e) => (e.target.style.borderColor = "#E5E7EB")}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
              }}
            >
              <button
                onClick={previousQuestion}
                disabled={currentSection === 0 && currentQuestion === 0}
                style={{
                  ...btnSecondaryStyle,
                  opacity:
                    currentSection === 0 && currentQuestion === 0 ? 0.5 : 1,
                  cursor:
                    currentSection === 0 && currentQuestion === 0
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <ChevronLeft size={20} /> Previous
              </button>

              <button onClick={nextQuestion} style={btnPrimaryStyle}>
                {currentSection === sections.length - 1 &&
                currentQuestion ===
                  sections[currentSection].questions.length - 1
                  ? "Discover My Story"
                  : "Next"}{" "}
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // GENERATING PAGE
  if (currentPage === "generating") {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ ...cardStyle, textAlign: "center", padding: "3rem" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <Sparkles
              style={{ color: colors.peru, animation: "pulse 2s infinite" }}
              size={64}
            />
          </div>
          <h2
            style={{
              fontSize: "1.875rem",
              fontWeight: "bold",
              color: colors.slate,
              marginBottom: "1rem",
            }}
          >
            Weaving Your Story...
          </h2>
          <p
            style={{
              fontSize: "1.125rem",
              color: colors.charcoal,
              marginBottom: "1rem",
            }}
          >
            We're reflecting on your responses and discovering the patterns in
            your freelance journey.
          </p>
          <p
            style={{
              fontSize: "0.875rem",
              fontStyle: "italic",
              color: colors.sage,
            }}
          >
            This moment is worth the wait.
          </p>
        </div>
      </div>
    );
  }

  // STORY PAGE
  if (currentPage === "story") {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, borderTop: `4px solid ${colors.peru}` }}>
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <div
              style={{
                display: "inline-block",
                padding: "1rem",
                background: `${colors.peru}20`,
                borderRadius: "50%",
                marginBottom: "1rem",
              }}
            >
              <Sparkles style={{ color: colors.peru }} size={48} />
            </div>
            <h2
              style={{
                fontSize: "2rem",
                fontWeight: "bold",
                color: colors.slate,
              }}
            >
              Your Freelance Story
            </h2>
          </div>

          <div
            style={{
              background: `linear-gradient(to bottom right, ${colors.cream}, ${colors.warmCream})`,
              border: `1px solid ${colors.peru}`,
              borderRadius: "0.75rem",
              padding: "2rem",
              marginBottom: "2rem",
            }}
          >
            <p
              style={{
                color: colors.charcoal,
                fontSize: "1.125rem",
                lineHeight: "1.75",
                marginBottom: "1.5rem",
              }}
            >
              Thank you for taking this journey of reflection. Your responses
              reveal a thoughtful, dedicated professional who is navigating the
              unique path of freelance environmental education with intention
              and insight.
            </p>
            <p
              style={{
                color: colors.charcoal,
                fontSize: "1.125rem",
                lineHeight: "1.75",
              }}
            >
              Your path is uniquely yours. The insights you've captured here are
              a snapshot of where you are right now—refer back to them when you
              need clarity, when you're making decisions, or when you want to
              measure how far you've come. This reflection is a gift you've
              given yourself.
            </p>
          </div>

          <div
            style={{
              background: `${colors.powder}20`,
              border: `1px solid ${colors.powder}`,
              borderRadius: "0.75rem",
              padding: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            <h3
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                color: colors.slate,
                fontWeight: "600",
                marginBottom: "0.75rem",
              }}
            >
              <Heart style={{ color: colors.peru }} size={20} />
              What Now?
            </h3>
            <p
              style={{
                color: colors.charcoal,
                fontSize: "0.875rem",
                marginBottom: "1rem",
              }}
            >
              Take a moment to sit with this reflection. What surprises you?
              What feels deeply true? Where do you want to go from here?
            </p>
            <button
              onClick={downloadResponses}
              style={{
                ...btnSecondaryStyle,
                background: colors.peru,
                color: "white",
                border: "none",
              }}
            >
              <Download size={20} /> Download Your Complete Story
            </button>
          </div>

          <div style={{ borderTop: `2px solid #E5E7EB`, paddingTop: "1.5rem" }}>
            <h3
              style={{
                color: colors.slate,
                fontWeight: "600",
                fontSize: "1.125rem",
                marginBottom: "0.75rem",
              }}
            >
              Want to contribute to something bigger?
            </h3>
            <p style={{ color: colors.charcoal, marginBottom: "1rem" }}>
              Your insights could help build a stronger community of freelance
              environmental educators. Would you be willing to share your
              responses (anonymously) for research on freelancing in
              environmental education?
            </p>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                onClick={() => setCurrentPage("research-invite")}
                style={{ ...btnPrimaryStyle, flex: 1 }}
              >
                Yes, I'd Like to Contribute
              </button>
              <button
                onClick={() => setCurrentPage("thankyou-personal")}
                style={{ ...btnSecondaryStyle, flex: 1 }}
              >
                No Thanks
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // RESEARCH INVITE PAGE
  if (currentPage === "research-invite") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2
            style={{
              fontSize: "1.75rem",
              fontWeight: "bold",
              color: colors.slate,
              marginBottom: "1.5rem",
            }}
          >
            Help Build a Stronger Community
          </h2>

          <p
            style={{
              fontSize: "1.125rem",
              color: colors.charcoal,
              marginBottom: "1rem",
            }}
          >
            Your story matters. By sharing your responses, you'll contribute to
            research that aims to:
          </p>
          <ul
            style={{
              color: colors.charcoal,
              marginBottom: "1.5rem",
              paddingLeft: "1.5rem",
            }}
          >
            <li style={{ marginBottom: "0.5rem" }}>
              Better understand the freelance path in environmental education
            </li>
            <li style={{ marginBottom: "0.5rem" }}>
              Identify challenges and support needs
            </li>
            <li style={{ marginBottom: "0.5rem" }}>
              Facilitate partnerships between freelancers and organizations
            </li>
            <li>
              Highlight the unique value of community-based environmental
              education
            </li>
          </ul>

          <div
            style={{
              background: `${colors.sage}15`,
              border: `1px solid ${colors.sage}40`,
              borderRadius: "0.75rem",
              padding: "1.5rem",
              marginBottom: "1.5rem",
            }}
          >
            <h3
              style={{
                color: colors.sage,
                fontWeight: "600",
                marginBottom: "0.75rem",
              }}
            >
              About the Research
            </h3>
            <p style={{ color: colors.charcoal, marginBottom: "0.75rem" }}>
              <strong>Conducted by:</strong> TALATERRA (The Freelance Project)
              <br />
              <strong>Researcher:</strong> Tania Marien
            </p>
            <p style={{ color: colors.sage, fontSize: "0.875rem" }}>
              Tania Marien has been investigating the contributions freelance
              environmental educators make to communities since 2018. She is the
              publisher of Transferable Solutions and producer of the TALATERRA
              podcast.
            </p>
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <h3
              style={{
                color: colors.slate,
                fontWeight: "600",
                marginBottom: "0.75rem",
              }}
            >
              What You Should Know
            </h3>
            <ul
              style={{
                color: colors.charcoal,
                fontSize: "0.875rem",
                paddingLeft: "1.5rem",
              }}
            >
              <li style={{ marginBottom: "0.25rem" }}>
                Your participation is completely voluntary
              </li>
              <li style={{ marginBottom: "0.25rem" }}>
                Your responses will be kept confidential
              </li>
              <li style={{ marginBottom: "0.25rem" }}>
                Results will be shared in aggregate form
              </li>
              <li style={{ marginBottom: "0.25rem" }}>
                Anonymous quotes may be used to represent patterns in the data
              </li>
              <li style={{ marginBottom: "0.25rem" }}>
                No personally identifying information will be shared without
                your consent
              </li>
              <li>You may request removal of your data at any time</li>
            </ul>
          </div>

          <div style={{ display: "flex", gap: "1rem" }}>
            <button
              onClick={() => setCurrentPage("consent")}
              style={{ ...btnPrimaryStyle, flex: 1 }}
            >
              I'd Like to Contribute
            </button>
            <button
              onClick={() => setCurrentPage("thankyou-personal")}
              style={btnSecondaryStyle}
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CONSENT PAGE
  if (currentPage === "consent") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: colors.slate,
              marginBottom: "1.5rem",
            }}
          >
            Research Consent
          </h2>

          <div
            style={{
              maxHeight: "24rem",
              overflowY: "auto",
              paddingRight: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            {[
              {
                title: "Study Title",
                content: "Freelancing in Environmental Education",
              },
              {
                title: "Purpose",
                content:
                  "To describe who freelance environmental educators are, what they do, and how they create change in their communities.",
              },
              {
                title: "What is Involved",
                content:
                  "Sharing your survey responses already completed and optionally participating in a follow-up interview.",
              },
            ].map((section, idx) => (
              <div key={idx} style={{ marginBottom: "1rem" }}>
                <h3
                  style={{
                    fontWeight: "600",
                    color: colors.charcoal,
                    marginBottom: "0.25rem",
                  }}
                >
                  {section.title}
                </h3>
                <p style={{ color: colors.charcoal }}>{section.content}</p>
              </div>
            ))}

            <div style={{ marginBottom: "1rem" }}>
              <h3
                style={{
                  fontWeight: "600",
                  color: colors.charcoal,
                  marginBottom: "0.25rem",
                }}
              >
                Voluntary Participation
              </h3>
              <ul
                style={{
                  fontSize: "0.875rem",
                  paddingLeft: "1.5rem",
                  color: colors.charcoal,
                }}
              >
                <li>Participation is completely voluntary</li>
                <li>You may withdraw at any time</li>
              </ul>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <h3
                style={{
                  fontWeight: "600",
                  color: colors.charcoal,
                  marginBottom: "0.25rem",
                }}
              >
                Privacy and Data Use
              </h3>
              <ul
                style={{
                  fontSize: "0.875rem",
                  paddingLeft: "1.5rem",
                  color: colors.charcoal,
                }}
              >
                <li>Responses will be kept confidential</li>
                <li>Results shared in aggregate form</li>
                <li>Anonymous quotes may represent patterns in data</li>
                <li>No identifying information shared without consent</li>
                <li>You may request data removal anytime</li>
              </ul>
            </div>

            <div>
              <h3
                style={{
                  fontWeight: "600",
                  color: colors.charcoal,
                  marginBottom: "0.25rem",
                }}
              >
                Contact
              </h3>
              <p style={{ color: colors.charcoal }}>
                Questions? Contact us at{" "}
                <a
                  href="https://talaterra.com/contact"
                  style={{ color: "#2563EB" }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  talaterra.com/contact
                </a>
              </p>
            </div>
          </div>

          <div
            style={{
              background: "#FEF3C7",
              borderLeft: `4px solid #D97706`,
              padding: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <p
              style={{
                fontSize: "0.875rem",
                color: "#78350F",
                fontWeight: "600",
              }}
            >
              By clicking "I Agree," you indicate that:
            </p>
            <ul
              style={{
                fontSize: "0.875rem",
                paddingLeft: "1.5rem",
                marginTop: "0.5rem",
                color: "#78350F",
              }}
            >
              <li>You have read and understood this information</li>
              <li>You voluntarily agree to participate</li>
              <li>You understand you may withdraw at any time</li>
            </ul>
          </div>

          <div style={{ display: "flex", gap: "1rem" }}>
            <button
              onClick={() => setCurrentPage("research-invite")}
              style={btnSecondaryStyle}
            >
              Back
            </button>
            <button
              onClick={() => setCurrentPage("contact")}
              style={{ ...btnPrimaryStyle, flex: 1 }}
            >
              I Agree to Participate
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CONTACT PAGE
  if (currentPage === "contact") {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div
            style={{
              display: "inline-block",
              padding: "0.75rem",
              background: `${colors.sage}20`,
              borderRadius: "50%",
              marginBottom: "1rem",
            }}
          >
            <Check size={48} style={{ color: colors.sage }} />
          </div>
          <h2
            style={{
              fontSize: "1.875rem",
              fontWeight: "bold",
              color: colors.slate,
              marginBottom: "0.5rem",
            }}
          >
            Thank You!
          </h2>
          <p style={{ color: colors.sage, marginBottom: "2rem" }}>
            Your contribution will help build a stronger community.
          </p>

          <div
            style={{
              background: `${colors.powder}15`,
              border: `1px solid ${colors.powder}`,
              borderRadius: "0.75rem",
              padding: "1.5rem",
              marginBottom: "1.5rem",
              textAlign: "left",
            }}
          >
            <h3
              style={{
                color: colors.sage,
                fontWeight: "600",
                marginBottom: "0.75rem",
              }}
            >
              Contact Information (Optional)
            </h3>
            <p style={{ color: colors.charcoal, marginBottom: "1rem" }}>
              If you're willing to be contacted for clarification or a potential
              follow-up interview, please provide your information below.
            </p>

            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  color: colors.charcoal,
                  marginBottom: "0.25rem",
                }}
              >
                Full Name
              </label>
              <input
                type="text"
                value={contactInfo.fullName}
                onChange={(e) =>
                  setContactInfo((prev) => ({
                    ...prev,
                    fullName: e.target.value,
                  }))
                }
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "2px solid #E5E7EB",
                  borderRadius: "0.5rem",
                  fontSize: "1rem",
                }}
                placeholder="Your full name"
              />
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  color: colors.charcoal,
                  marginBottom: "0.25rem",
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                value={contactInfo.email}
                onChange={(e) =>
                  setContactInfo((prev) => ({ ...prev, email: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "2px solid #E5E7EB",
                  borderRadius: "0.5rem",
                  fontSize: "1rem",
                }}
                placeholder="your.email@example.com"
              />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={contactInfo.followUpInterview}
                  onChange={(e) =>
                    setContactInfo((prev) => ({
                      ...prev,
                      followUpInterview: e.target.checked,
                    }))
                  }
                  style={{
                    marginTop: "0.25rem",
                    width: "1.25rem",
                    height: "1.25rem",
                    accentColor: colors.sage,
                  }}
                />
                <span style={{ color: colors.charcoal }}>
                  I'm willing to participate in a follow-up interview to discuss
                  my responses in more depth
                </span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={contactInfo.generalContact}
                  onChange={(e) =>
                    setContactInfo((prev) => ({
                      ...prev,
                      generalContact: e.target.checked,
                    }))
                  }
                  style={{
                    marginTop: "0.25rem",
                    width: "1.25rem",
                    height: "1.25rem",
                    accentColor: colors.sage,
                  }}
                />
                <span style={{ color: colors.charcoal }}>
                  I'd like to receive general updates about this research and
                  TALATERRA resources
                </span>
              </label>
            </div>
          </div>

          <button onClick={submitToGoogleForm} style={btnPrimaryStyle}>
            Submit to Google Form
          </button>

          <p
            style={{
              fontSize: "0.875rem",
              color: colors.sage,
              textAlign: "center",
              marginTop: "1rem",
            }}
          >
            Your responses will be used according to the consent agreement you
            accepted. The form will open in a new tab.
          </p>
        </div>
      </div>
    );
  }

  // THANK YOU - RESEARCH
  if (currentPage === "thankyou-research") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                display: "inline-block",
                padding: "0.75rem",
                background: `${colors.sage}20`,
                borderRadius: "50%",
                marginBottom: "1rem",
              }}
            >
              <Check size={64} style={{ color: colors.sage }} />
            </div>
            <h2
              style={{
                fontSize: "1.875rem",
                fontWeight: "bold",
                color: colors.slate,
                marginBottom: "1rem",
              }}
            >
              Thank You for Your Contribution!
            </h2>
            <p
              style={{
                fontSize: "1.125rem",
                color: colors.charcoal,
                marginBottom: "2rem",
              }}
            >
              Your story and insights will help describe and support the
              freelance environmental education community.
            </p>
          </div>

          <div
            style={{
              background: `linear-gradient(to right, ${colors.sage}15, ${colors.powder}15)`,
              border: `2px solid ${colors.sage}30`,
              borderRadius: "0.5rem",
              padding: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            <h3
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: colors.charcoal,
                marginBottom: "1rem",
              }}
            >
              What Happens Next?
            </h3>
            <ul style={{ color: colors.charcoal }}>
              {[
                "Your survey responses will be analyzed alongside other participants' contributions",
                "If you indicated interest in a follow-up interview, you may be contacted via the information you provided",
                "Results from this research will be shared with the environmental education community in aggregate form",
                "Your insights may be represented through anonymous quotes that illustrate patterns in the data",
              ].map((item, idx) => (
                <li
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.75rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  <span style={{ color: colors.sage, fontWeight: "bold" }}>
                    •
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            style={{
              background: `${colors.powder}15`,
              borderLeft: `4px solid ${colors.powder}`,
              padding: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            <h3
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: colors.sage,
                marginBottom: "0.75rem",
              }}
            >
              Stay Connected
            </h3>
            <div style={{ color: colors.charcoal }}>
              <p style={{ marginBottom: "0.5rem" }}>
                <strong>TALATERRA Podcast:</strong> Hear conversations with
                other freelance environmental educators at{" "}
                <a
                  href="https://talaterra.com/podcast"
                  style={{ color: "#2563EB" }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  talaterra.com/podcast
                </a>
              </p>
              <p>
                <strong>Questions or Feedback:</strong> Contact us at{" "}
                <a
                  href="https://talaterra.com/contact"
                  style={{ color: "#2563EB" }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  talaterra.com/contact
                </a>
              </p>
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
            <p style={{ color: colors.charcoal }}>
              Thank you for being part of this important work to better
              understand and support freelance environmental educators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // THANK YOU - PERSONAL
  if (currentPage === "thankyou-personal") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                display: "inline-block",
                padding: "0.75rem",
                background: `${colors.peru}20`,
                borderRadius: "50%",
                marginBottom: "1rem",
              }}
            >
              <Heart size={64} style={{ color: colors.peru }} />
            </div>
            <h2
              style={{
                fontSize: "1.875rem",
                fontWeight: "bold",
                color: colors.slate,
                marginBottom: "1rem",
              }}
            >
              Thank You for Your Reflection
            </h2>
            <p
              style={{
                fontSize: "1.125rem",
                color: colors.charcoal,
                marginBottom: "2rem",
              }}
            >
              We hope this journey of self-discovery brought you clarity and
              insight into your freelance path.
            </p>
          </div>

          <div
            style={{
              background: `linear-gradient(to right, ${colors.cream}, ${colors.warmCream})`,
              border: `1px solid ${colors.peru}`,
              borderRadius: "0.75rem",
              padding: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            <h3
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: colors.slate,
                marginBottom: "0.75rem",
              }}
            >
              Your Story is Yours
            </h3>
            <p style={{ color: colors.charcoal, marginBottom: "1rem" }}>
              You've taken time to reflect deeply on your journey. That takes
              courage and intention. Keep your downloaded story close—refer back
              to it when you need clarity or when you're making big decisions.
            </p>
            <p style={{ color: colors.charcoal }}>
              Your path as a freelance environmental educator is unique and
              valuable. Trust the wisdom you've gained.
            </p>
          </div>

          <div
            style={{
              background: `${colors.powder}15`,
              borderLeft: `4px solid ${colors.powder}`,
              padding: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            <h3
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: colors.sage,
                marginBottom: "0.75rem",
              }}
            >
              Stay Connected
            </h3>
            <div style={{ color: colors.charcoal }}>
              <p style={{ marginBottom: "0.5rem" }}>
                <strong>TALATERRA Podcast:</strong> Hear stories from other
                freelance environmental educators at{" "}
                <a
                  href="https://talaterra.com/podcast"
                  style={{ color: "#2563EB" }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  talaterra.com/podcast
                </a>
              </p>
              <p>
                <strong>Get in Touch:</strong> Have thoughts to share? Reach out
                at{" "}
                <a
                  href="https://talaterra.com/contact"
                  style={{ color: "#2563EB" }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  talaterra.com/contact
                </a>
              </p>
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
            <p style={{ color: colors.charcoal, fontStyle: "italic" }}>
              🌿 May your freelance journey continue to flourish 🌿
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <div>Loading...</div>;
};

export default YourFreelanceStory;
